import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { Check, X } from 'lucide-react';

const ToastContext = createContext(null);
const TOAST_DURATION_MS = 3000;
const MAX_VISIBLE_TOASTS = 5;

/**
 * @param {string} message - 토스트 메시지
 * @param {'success'|'error'} [type='success'] - success: 파란 원 + 체크, error: 빨간 원 + X (배송기사/앱 토스트와 동일 UI)
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  useEffect(() => {
    return () => {
      timersRef.current.forEach((tid) => clearTimeout(tid));
      timersRef.current.clear();
    };
  }, []);

  const showToast = (message, type = 'success') => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts((prev) => {
      const next = [...prev, { id, message, type: type === 'error' ? 'error' : 'success' }];
      return next.length > MAX_VISIBLE_TOASTS ? next.slice(-MAX_VISIBLE_TOASTS) : next;
    });
    const timerId = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      timersRef.current.delete(id);
    }, TOAST_DURATION_MS);
    timersRef.current.set(id, timerId);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* 상단 가운데 토스트 - 배송기사(앱) 토스트와 동일 UI: 흰 카드, 성공=파란 체크 / 실패=빨간 X */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[10000] flex flex-col gap-3 pointer-events-none items-center w-auto max-w-[768px] px-4 box-border">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="flex items-center gap-3 py-3 px-4 rounded-xl bg-white text-[#111827] text-[14px] font-medium shadow-[0_4px_12px_rgba(0,0,0,0.15)] pointer-events-auto w-full"
          >
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: toast.type === 'error' ? '#F04452' : '#3182F6' }}
            >
              {toast.type === 'error' ? (
                <X size={14} className="text-white" />
              ) : (
                <Check size={14} className="text-white" />
              )}
            </div>
            <span className="overflow-hidden text-ellipsis" style={{ whiteSpace: toast.message.includes('\n') ? 'pre-line' : 'nowrap' }}>{toast.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) return { showToast: () => {} };
  return ctx;
}
